import { makeAutoObservable } from 'mobx';
import { ICredentialsStore } from '../../../integration-store';
import FedexV2RegistrationStore from './fedex-v2-registration-store';
import {
    FedexV2CredentialsPayload,
    IFedexV2CredentialsChanges,
} from './fedex-v2-credentials.types';

const REGISTRATION_CANCELLED_ERROR = 'FedEx account registration was cancelled';

class FedexV2CredentialsStore implements ICredentialsStore {
    private _changes: IFedexV2CredentialsChanges = {};
    private _registration: FedexV2RegistrationStore | null = null;
    private _pendingReject: ((reason: Error) => void) | null = null;

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    get registration() {
        return this._registration;
    }

    get registrationWizardOpen() {
        return this._registration !== null;
    }

    get apiKey() {
        return this._changes.api_key;
    }

    get internalName() {
        return this._changes.internal_name;
    }

    setInternalName(value: string) {
        this._changes.internal_name = value;
    }

    setApiKey(value: string) {
        this._changes.api_key = value;
    }

    async getCredentials(isSandbox = false): Promise<FedexV2CredentialsPayload> {
        this.abortPendingRegistration();

        try {
            const payload = await this.openRegistrationWizard(isSandbox);
            this.applyPayload(payload);
            return payload;
        } catch (error) {
            this.clearPersistedSecrets();
            throw error;
        }
    }

    private openRegistrationWizard(isSandbox: boolean): Promise<FedexV2CredentialsPayload> {
        return new Promise((resolve, reject) => {
            this._pendingReject = reject;

            const registration = new FedexV2RegistrationStore(isSandbox, {
                onSuccess: (payload) => {
                    this.endRegistrationWizard();
                    resolve(payload);
                },
                onCancel: () => {
                    this.endRegistrationWizard();
                    reject(new Error(REGISTRATION_CANCELLED_ERROR));
                },
            });

            if (this._changes.api_key) {
                registration.address.setAccountNumber(this._changes.api_key);
            }

            this._registration = registration;
        });
    }

    private abortPendingRegistration() {
        if (!this._registration && !this._pendingReject) {
            return;
        }

        const reject = this._pendingReject;
        this._registration?.abandon();
        this.endRegistrationWizard();
        this.clearPersistedSecrets();
        reject?.(new Error(REGISTRATION_CANCELLED_ERROR));
    }

    private endRegistrationWizard() {
        this._registration = null;
        this._pendingReject = null;
    }

    private applyPayload(payload: FedexV2CredentialsPayload) {
        this._changes.api_key = payload.api_key;
        this._changes.child_key = payload.child_key;
        this._changes.child_secret = payload.child_secret;
        this._changes.webservice_client_id = undefined;
        this._changes.webservice_client_secret = undefined;
    }

    private clearPersistedSecrets() {
        this._changes = {};
    }
}

export default FedexV2CredentialsStore;
